import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Collapse, Form, Row } from 'react-bootstrap';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import CurrencyInput from 'react-currency-input';
import Select from 'react-select';
import moment from 'moment';
import { CondicoesEspeciaisStatus } from '~/constant';
import { customStylesSm, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { formatCurrency } from '../utils';

var OPCOES_ISENCAO = [
  { value: false, label: 'Não' },
  { value: true, label: 'Sim' },
];

function formatDataMesAno(valor) {
  if (!valor) return '';
  var nums = valor.replace(/\D/g, '');
  if (nums.length > 2) {
    return nums.slice(0, 2) + '/' + nums.slice(2, 6);
  }
  return nums;
}

function badgeCondicoes(c) {
  var partes = [];
  if (c.isIsencao) partes.push('Isenção');
  if (c.financiamento && c.financiamento.isAtivo) partes.push('Carência ' + c.financiamento.meses + 'm');
  if (c.saldoCorrecao && c.saldoCorrecao.isAtivo) partes.push('Saldo Correção');
  if (c.valorLote && c.valorLote.isAtivo) partes.push('Desconto Lote ' + formatCurrency(c.valorLote.porcentagem) + '%');
  return partes.join(' · ');
}

function statusCondicaoLabel(status) {
  if (status === CondicoesEspeciaisStatus.PendenteDiretoria) return 'Pendente Diretoria';
  if (status === CondicoesEspeciaisStatus.PendenteGerente) return 'Pendente Gerente';
  if (status === CondicoesEspeciaisStatus.Contraproposta) return 'Contraproposta';
  if (status === CondicoesEspeciaisStatus.SolicitacaoAprovada) return 'Aprovada';
  return 'Sem status';
}

function statusCondicaoVariant(status) {
  if (status === CondicoesEspeciaisStatus.PendenteDiretoria) return 'warning';
  if (status === CondicoesEspeciaisStatus.PendenteGerente) return 'info';
  if (status === CondicoesEspeciaisStatus.Contraproposta) return 'warning';
  if (status === CondicoesEspeciaisStatus.SolicitacaoAprovada) return 'success';
  return 'secondary';
}

export default function CondicoesEspeciaisCompleto(props) {
  var valorVendaOriginal = props.valorVendaOriginal || 0;
  var condicoesAplicadas = props.condicoesAplicadas || null;
  var onAplicar = props.onAplicar;
  var onRemover = props.onRemover;
  var resetSignal = props.resetSignal;
  var dataBase = props.dataBase;

  var [aberta, setAberta] = useState(true);
  var [isIsencao, setIsIsencao] = useState(false);
  var [financ, setFinanc] = useState({ isAtivo: false, meses: '' });
  var [saldoCorr, setSaldoCorr] = useState({ isAtivo: false, valor: '', data: '' });
  var [valorLote, setValorLote] = useState({ isAtivo: false, valorSugerido: '', porcentagem: '' });
  var [descricao, setDescricao] = useState('');
  var [erros, setErros] = useState({});

  // Reset signal do pai: limpa estado local quando o pai zera condicoesAplicadas
  // por algum gatilho externo (troca de VV, recarregar dados etc).
  useEffect(function() {
    setIsIsencao(false);
    setFinanc({ isAtivo: false, meses: '' });
    setSaldoCorr({ isAtivo: false, valor: '', data: '' });
    setValorLote({ isAtivo: false, valorSugerido: '', porcentagem: '' });
    setDescricao('');
    setErros({});
  }, [resetSignal]);

  var isAtiva = !!condicoesAplicadas;
  var temOpcao = isIsencao || financ.isAtivo || saldoCorr.isAtivo || valorLote.isAtivo;

  var selectStyles = useMemo(function() {
    return Object.assign({}, customStylesSm, {
      menuPortal: function(b) { return Object.assign({}, b, { zIndex: 99999 }); },
    });
  }, []);

  var mesesLabel = useMemo(function() {
    var m = parseInt(financ.meses, 10);
    if (!m || m <= 0) return '';
    return moment(dataBase).add(m, 'months').locale('pt-br').format('MMMM [de] YYYY');
  }, [financ.meses, dataBase]);

  var calcValorComPct = useCallback(function(pct) {
    return Math.round(valorVendaOriginal * (1 - pct / 100) * 100) / 100;
  }, [valorVendaOriginal]);

  var calcPctDeValor = useCallback(function(valSugerido) {
    if (!valorVendaOriginal) return 0;
    return parseFloat(((Math.abs(valorVendaOriginal - valSugerido) / valorVendaOriginal) * 100).toFixed(3));
  }, [valorVendaOriginal]);

  useEffect(function() {
    if (!aberta || !condicoesAplicadas) return;
    setIsIsencao(condicoesAplicadas.isIsencao || false);
    setFinanc({
      isAtivo: !!(condicoesAplicadas.financiamento && condicoesAplicadas.financiamento.isAtivo),
      meses: condicoesAplicadas.financiamento ? String(condicoesAplicadas.financiamento.meses || '') : '',
    });
    setSaldoCorr({
      isAtivo: !!(condicoesAplicadas.saldoCorrecao && condicoesAplicadas.saldoCorrecao.isAtivo),
      valor: condicoesAplicadas.saldoCorrecao ? (condicoesAplicadas.saldoCorrecao.valor || '') : '',
      data: condicoesAplicadas.saldoCorrecao ? (condicoesAplicadas.saldoCorrecao.data || '') : '',
    });
    setValorLote({
      isAtivo: !!(condicoesAplicadas.valorLote && condicoesAplicadas.valorLote.isAtivo),
      valorSugerido: condicoesAplicadas.valorLote ? (condicoesAplicadas.valorLote.valorSugerido || '') : '',
      porcentagem: condicoesAplicadas.valorLote ? (condicoesAplicadas.valorLote.porcentagem || '') : '',
    });
    setDescricao(condicoesAplicadas.descricao || '');
  }, [aberta, condicoesAplicadas]);

  var handleAplicar = function() {
    var novosErros = {};
    if (!temOpcao) novosErros.geral = 'Preencha pelo menos uma opção.';
    var descClean = (descricao || '').replace(/\s/g, '');
    if (descClean.length < 20) novosErros.descricao = 'Descrição deve ter no mínimo 20 caracteres.';
    if (financ.isAtivo && (!financ.meses || parseInt(financ.meses, 10) <= 0)) {
      novosErros.financMeses = 'Informe o prazo em meses.';
    }
    if (saldoCorr.isAtivo) {
      if (!saldoCorr.valor || parseFloat(saldoCorr.valor) <= 0) novosErros.saldoValor = 'Informe o valor.';
      if (!saldoCorr.data || !/^(0[1-9]|1[0-2])\/\d{4}$/.test(saldoCorr.data)) novosErros.saldoData = 'Data inválida (MM/AAAA).';
    }
    if (valorLote.isAtivo) {
      var vsSug = parseFloat(valorLote.valorSugerido);
      if (!vsSug || vsSug <= 0) novosErros.loteValor = 'Informe o valor.';
      else if (vsSug >= valorVendaOriginal) novosErros.loteValor = 'Valor deve ser menor que o VV original.';
      if (!valorLote.porcentagem || parseFloat(valorLote.porcentagem) <= 0) novosErros.lotePct = 'Informe o percentual.';
    }
    setErros(novosErros);
    if (Object.keys(novosErros).length > 0) return;

    onAplicar({
      isIsencao: isIsencao,
      financiamento: {
        isAtivo: financ.isAtivo,
        meses: parseInt(financ.meses, 10) || 0,
        terminoLabel: mesesLabel,
      },
      saldoCorrecao: {
        isAtivo: saldoCorr.isAtivo,
        valor: parseFloat(saldoCorr.valor) || 0,
        data: saldoCorr.data,
      },
      valorLote: {
        isAtivo: valorLote.isAtivo,
        valorSugerido: parseFloat(valorLote.valorSugerido) || 0,
        porcentagem: parseFloat(valorLote.porcentagem) || 0,
      },
      descricao: descricao,
    });
    setAberta(false);
  };

  var handleRemover = function() {
    if (onRemover && onRemover() === false) return;
    setIsIsencao(false);
    setFinanc({ isAtivo: false, meses: '' });
    setSaldoCorr({ isAtivo: false, valor: '', data: '' });
    setValorLote({ isAtivo: false, valorSugerido: '', porcentagem: '' });
    setDescricao('');
    setErros({});
  };

  return (
    <Card className="mb-2 h-100">
      <Card.Body className="py-2">
        <div
          className="d-flex justify-content-between align-items-center"
          onClick={function() { setAberta(function(v) { return !v; }); }}
          style={{ cursor: 'pointer' }}
        >
          <h6 className="mb-0 text-primary">
            <strong>Condições Especiais</strong>
            {isAtiva && (
              <span
                className="ml-2"
                style={{
                  background: '#ffc107',
                  color: '#212529',
                  fontSize: '0.7rem',
                  padding: '2px 6px',
                  borderRadius: 4,
                  fontWeight: 600,
                }}
              >
                {badgeCondicoes(condicoesAplicadas)}
              </span>
            )}
            {isAtiva && condicoesAplicadas && condicoesAplicadas.status && (
              <span
                className="ml-2"
                style={{
                  background: '#e9ecef',
                  color: '#212529',
                  fontSize: '0.7rem',
                  padding: '2px 6px',
                  borderRadius: 4,
                  fontWeight: 600,
                }}
              >
                {statusCondicaoLabel(condicoesAplicadas.status)}
              </span>
            )}
          </h6>
          {aberta
            ? <FaChevronUp size={14} className="text-primary" />
            : <FaChevronDown size={14} className="text-primary" />}
        </div>

        <Collapse in={aberta}>
          <div className="mt-2">
            {erros.geral && <div className="text-danger small mb-2">{erros.geral}</div>}

            {/* Isenção da taxa de cessão */}
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1">Isenção da taxa de cessão</Form.Label>
              <Select
                placeholder="Selecione..."
                options={OPCOES_ISENCAO}
                value={OPCOES_ISENCAO.find(function(o) { return o.value === isIsencao; }) || OPCOES_ISENCAO[0]}
                onChange={function(opt) {
                  setIsIsencao(opt.value);
                  setErros(function(e) { return Object.assign({}, e, { geral: undefined }); });
                }}
                styles={selectStyles}
                components={{ DropdownIndicator: DropdownIndicator }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Form.Group>

            {/* Carência do financiamento */}
            <Form.Check
              id="ce-carencia-financiamento"
              type="checkbox"
              label="Carência do financiamento"
              checked={financ.isAtivo}
              onChange={function(e) {
                var checked = e.target.checked;
                setFinanc(function(f) { return Object.assign({}, f, { isAtivo: checked, meses: checked ? f.meses : '' }); });
                if (!checked) setErros(function(e) { return Object.assign({}, e, { financMeses: undefined }); });
              }}
              className="mb-1"
            />
            {financ.isAtivo && (
              <Row className="mb-2">
                <Col md={4} xs={6}>
                  <Form.Label className="small mb-1">Prazo (meses)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    min="1"
                    value={financ.meses}
                    onChange={function(e) {
                      var val = e.target.value;
                      setFinanc(function(f) { return Object.assign({}, f, { meses: val }); });
                      setErros(function(e) { return Object.assign({}, e, { financMeses: undefined }); });
                    }}
                    isInvalid={!!erros.financMeses}
                  />
                  {erros.financMeses && (
                    <Form.Control.Feedback type="invalid">{erros.financMeses}</Form.Control.Feedback>
                  )}
                </Col>
                {mesesLabel && (
                  <Col md={8} xs={6} className="d-flex align-items-end pb-1">
                    <small className="text-muted">
                      Início aprox. das parcelas: <strong>{mesesLabel}</strong>
                    </small>
                  </Col>
                )}
              </Row>
            )}

            {/* Saldo com Correção */}
            <Form.Check
              id="ce-saldo-correcao"
              type="checkbox"
              label="Saldo com Correção"
              checked={saldoCorr.isAtivo}
              onChange={function(e) {
                var checked = e.target.checked;
                setSaldoCorr(function(s) {
                  return Object.assign({}, s, { isAtivo: checked, valor: checked ? s.valor : '', data: checked ? s.data : '' });
                });
                if (!checked) setErros(function(e) { return Object.assign({}, e, { saldoValor: undefined, saldoData: undefined }); });
              }}
              className="mb-1"
            />
            {saldoCorr.isAtivo && (
              <Row className="mb-2">
                <Col md={4} xs={6}>
                  <Form.Label className="small mb-1">Valor do Saldo</Form.Label>
                  <CurrencyInput
                    className={'form-control form-control-sm' + (erros.saldoValor ? ' is-invalid' : '')}
                    value={saldoCorr.valor}
                    decimalSeparator=","
                    thousandSeparator="."
                    prefix="R$ "
                    onChangeEvent={function(e, m, fv) {
                      setSaldoCorr(function(s) { return Object.assign({}, s, { valor: fv }); });
                      setErros(function(e) { return Object.assign({}, e, { saldoValor: undefined }); });
                    }}
                  />
                  {erros.saldoValor && <div className="text-danger small mt-1">{erros.saldoValor}</div>}
                </Col>
                <Col md={4} xs={6}>
                  <Form.Label className="small mb-1">Data (MM/AAAA)</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="02/2026"
                    value={saldoCorr.data}
                    maxLength={7}
                    onChange={function(e) {
                      var formatted = formatDataMesAno(e.target.value);
                      setSaldoCorr(function(s) { return Object.assign({}, s, { data: formatted }); });
                      setErros(function(e) { return Object.assign({}, e, { saldoData: undefined }); });
                    }}
                    isInvalid={!!erros.saldoData}
                  />
                  {erros.saldoData && (
                    <Form.Control.Feedback type="invalid">{erros.saldoData}</Form.Control.Feedback>
                  )}
                </Col>
              </Row>
            )}

            {/* Desconto no Valor do Lote */}
            <Form.Check
              id="ce-desconto-valor-lote"
              type="checkbox"
              label="Desconto no Valor do Lote"
              checked={valorLote.isAtivo}
              onChange={function(e) {
                var checked = e.target.checked;
                setValorLote(function(v) {
                  return Object.assign({}, v, { isAtivo: checked, valorSugerido: checked ? v.valorSugerido : '', porcentagem: checked ? v.porcentagem : '' });
                });
                if (!checked) setErros(function(e) { return Object.assign({}, e, { loteValor: undefined, lotePct: undefined }); });
              }}
              className="mb-1"
            />
            {valorLote.isAtivo && (
              <Row className="mb-2">
                <Col md={4} xs={6}>
                  <Form.Label className="small mb-1">Valor Sugerido</Form.Label>
                  <CurrencyInput
                    className={'form-control form-control-sm' + (erros.loteValor ? ' is-invalid' : '')}
                    value={valorLote.valorSugerido}
                    decimalSeparator=","
                    thousandSeparator="."
                    prefix="R$ "
                    onChangeEvent={function(e, m, fv) {
                      var pct = fv > 0 ? calcPctDeValor(fv) : '';
                      setValorLote(function(v) { return Object.assign({}, v, { valorSugerido: fv, porcentagem: pct }); });
                      setErros(function(e) { return Object.assign({}, e, { loteValor: undefined, lotePct: undefined }); });
                    }}
                  />
                  {erros.loteValor && <div className="text-danger small mt-1">{erros.loteValor}</div>}
                </Col>
                <Col md={3} xs={6}>
                  <Form.Label className="small mb-1">Desconto (%)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    min="0.001"
                    max="99.999"
                    step="0.001"
                    placeholder="%"
                    value={valorLote.porcentagem}
                    onChange={function(e) {
                      var pct = e.target.value;
                      setValorLote(function(v) { return Object.assign({}, v, { porcentagem: pct }); });
                      setErros(function(e) { return Object.assign({}, e, { lotePct: undefined }); });
                    }}
                    onBlur={function() {
                      var pct = parseFloat(valorLote.porcentagem);
                      if (pct > 0 && pct < 100) {
                        setValorLote(function(v) { return Object.assign({}, v, { valorSugerido: calcValorComPct(pct) }); });
                      }
                    }}
                    isInvalid={!!erros.lotePct}
                  />
                  {erros.lotePct && (
                    <Form.Control.Feedback type="invalid">{erros.lotePct}</Form.Control.Feedback>
                  )}
                </Col>
                {valorVendaOriginal > 0 && parseFloat(valorLote.valorSugerido) > 0 && (
                  <Col md={5} xs={12} className="d-flex align-items-end pb-1">
                    <small className="text-muted">
                      R$ {formatCurrency(valorVendaOriginal)}
                      {' → '}
                      <strong className="text-danger">
                        R$ {formatCurrency(parseFloat(valorLote.valorSugerido) || 0)}
                      </strong>
                    </small>
                  </Col>
                )}
              </Row>
            )}

            {/* Descrição */}
            {temOpcao && (
              <Form.Group className="mb-2">
                <Form.Label className="small mb-1">
                  Descrição da condição{' '}
                  <span className="text-muted">(mín. 20 caracteres sem espaço)</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  size="sm"
                  value={descricao}
                  onChange={function(e) {
                    setDescricao(e.target.value);
                    setErros(function(err) { return Object.assign({}, err, { descricao: undefined }); });
                  }}
                  isInvalid={!!erros.descricao}
                  placeholder="Descreva o motivo da condição especial..."
                />
                {erros.descricao && (
                  <Form.Control.Feedback type="invalid">{erros.descricao}</Form.Control.Feedback>
                )}
              </Form.Group>
            )}

            {/* Botões */}
            <div className="d-flex align-items-center">
              {!isAtiva ? (
                <Button
                  size="sm"
                  variant="warning"
                  onClick={handleAplicar}
                  disabled={!temOpcao}
                >
                  Aplicar Condições
                </Button>
              ) : (
                <>
                  <Button size="sm" variant="warning" onClick={handleAplicar} className="mr-2">
                    Reeditar
                  </Button>
                  <Button size="sm" variant="outline-secondary" onClick={handleRemover}>
                    Remover Condições
                  </Button>
                </>
              )}
            </div>

            {isAtiva && condicoesAplicadas && (
              <div className="rounded p-2 mt-2" style={{ background: '#fff3cd', border: '1px solid #ffc107' }}>
                <small>
                  {condicoesAplicadas.isIsencao && (
                    <span className="d-block">✓ Isenção da taxa de cessão</span>
                  )}
                  {condicoesAplicadas.financiamento && condicoesAplicadas.financiamento.isAtivo && (
                    <span className="d-block">
                      ✓ Carência de {condicoesAplicadas.financiamento.meses} meses
                      {condicoesAplicadas.financiamento.terminoLabel && (
                        ' (início aprox.: ' + condicoesAplicadas.financiamento.terminoLabel + ')'
                      )}
                    </span>
                  )}
                  {condicoesAplicadas.saldoCorrecao && condicoesAplicadas.saldoCorrecao.isAtivo && (
                    <span className="d-block">
                      ✓ Saldo com correção: R$ {formatCurrency(condicoesAplicadas.saldoCorrecao.valor)}
                      {' em '}{condicoesAplicadas.saldoCorrecao.data}
                    </span>
                  )}
                  {condicoesAplicadas.valorLote && condicoesAplicadas.valorLote.isAtivo && (
                    <span className="d-block">
                      ✓ Desconto no lote: {formatCurrency(condicoesAplicadas.valorLote.porcentagem)}%
                      {' → R$ '}{formatCurrency(condicoesAplicadas.valorLote.valorSugerido)}
                    </span>
                  )}
                </small>
              </div>
            )}

            {isAtiva && condicoesAplicadas && condicoesAplicadas.avisoAlcada && (
              <Alert
                variant={statusCondicaoVariant(condicoesAplicadas.status)}
                className="py-1 px-2 mt-2 mb-0 small"
              >
                <strong>{statusCondicaoLabel(condicoesAplicadas.status)}:</strong>{' '}
                {condicoesAplicadas.avisoAlcada}
              </Alert>
            )}
          </div>
        </Collapse>
      </Card.Body>
    </Card>
  );
}
